import { keyOf } from "../types/state";

export interface TableData {
  columns: string[];
  rows: Record<string, string>[];
}

export interface SheetMeta {
  name: string;
  tables: string[];
}

export function checkCompatibility(): { ok: boolean; reason?: string } {
  try {
    if (!Office.context?.requirements?.isSetSupported("ExcelApi", "1.4")) {
      return {
        ok: false,
        reason:
          "Ta version d'Excel est trop ancienne. L'add-in nécessite Excel 2016 (à jour) ou plus récent (ExcelApi 1.4).",
      };
    }
  } catch {
    return { ok: false, reason: "Impossible de détecter la version d'Excel." };
  }
  return { ok: true };
}

export function isApiSupported(version: string): boolean {
  try {
    return Office.context.requirements.isSetSupported("ExcelApi", version);
  } catch {
    return false;
  }
}

function normalizeValue(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "number") {
    if (!Number.isFinite(v)) return String(v);
    if (Number.isInteger(v)) return String(v);
    return v.toFixed(6).replace(/0+$/, "").replace(/\.$/, "");
  }
  if (v instanceof Date) return v.toISOString();
  if (typeof v === "boolean") return v ? "TRUE" : "FALSE";
  return String(v).trim();
}

export async function listSheetsAndTables(storageSheetName: string): Promise<SheetMeta[]> {
  return Excel.run(async (ctx) => {
    const sheets = ctx.workbook.worksheets;
    sheets.load("items/name");
    await ctx.sync();

    const result: SheetMeta[] = [];
    for (const sheet of sheets.items) {
      if (sheet.name === storageSheetName) continue;
      const tables = sheet.tables;
      tables.load("items/name");
      await ctx.sync();
      result.push({
        name: sheet.name,
        tables: tables.items.map((t) => t.name),
      });
    }
    return result;
  });
}

export async function readHeaders(
  sheetName: string,
  tableName: string | null,
  headerRowCount = 1,
  topRowsToSkip = 0
): Promise<string[]> {
  const data = await readDataTable(sheetName, tableName, [], headerRowCount, topRowsToSkip);
  return data.columns;
}

function propagateMergedValues(
  values: unknown[][],
  rangeRow: number,
  rangeCol: number,
  mergedAreas: Array<{ rowIndex: number; columnIndex: number; rowCount: number; columnCount: number }>
): void {
  for (const area of mergedAreas) {
    const relR = area.rowIndex - rangeRow;
    const relC = area.columnIndex - rangeCol;
    if (relR < 0 || relC < 0 || relR >= values.length) continue;
    const topLeft = values[relR]?.[relC];
    for (let r = 0; r < area.rowCount; r++) {
      for (let c = 0; c < area.columnCount; c++) {
        const tr = relR + r;
        const tc = relC + c;
        if (tr >= values.length) continue;
        if (!values[tr]) continue;
        if (r === 0 && c === 0) continue;
        values[tr][tc] = topLeft;
      }
    }
  }
}

function buildHeaders(values: unknown[][], headerRowCount: number): string[] {
  if (headerRowCount <= 1) {
    return (values[0] ?? []).map((h) => String(h ?? "").trim());
  }
  const cols = (values[0] ?? []).length;
  const headers: string[] = [];
  for (let c = 0; c < cols; c++) {
    const parts: string[] = [];
    for (let r = 0; r < headerRowCount; r++) {
      const v = String(values[r]?.[c] ?? "").trim();
      if (v && !parts.includes(v)) parts.push(v);
    }
    headers.push(parts.join(" / "));
  }
  return headers;
}

export async function readDataTable(
  sheetName: string,
  tableName: string | null,
  ignoreColumns: string[] = [],
  headerRowCount = 1,
  topRowsToSkip = 0
): Promise<TableData> {
  return Excel.run(async (ctx) => {
    const sheet = ctx.workbook.worksheets.getItem(sheetName);
    let range: Excel.Range;
    if (tableName) {
      range = sheet.tables.getItem(tableName).getRange();
    } else {
      range = sheet.getUsedRange(true);
    }
    range.load(["values", "rowIndex", "columnIndex", "rowCount", "columnCount"]);

    let mergedAreas: Array<{ rowIndex: number; columnIndex: number; rowCount: number; columnCount: number }> = [];
    try {
      const merged = range.getMergedAreasOrNullObject();
      merged.areas.load("items/rowIndex,items/columnIndex,items/rowCount,items/columnCount");
      await ctx.sync();
      if (!merged.isNullObject) {
        mergedAreas = merged.areas.items.map((a) => ({
          rowIndex: a.rowIndex,
          columnIndex: a.columnIndex,
          rowCount: a.rowCount,
          columnCount: a.columnCount,
        }));
      }
    } catch {
      await ctx.sync();
    }

    const rawValues = (range.values ?? []) as unknown[][];
    if (rawValues.length === 0) return { columns: [], rows: [] };

    const values: unknown[][] = rawValues.map((r) => [...r]);
    if (mergedAreas.length > 0) {
      propagateMergedValues(values, range.rowIndex, range.columnIndex, mergedAreas);
    }

    const skip = tableName ? 0 : Math.max(0, topRowsToSkip);
    const usable = values.slice(skip);
    const headers = buildHeaders(usable, headerRowCount);
    const dataRows = usable.slice(headerRowCount);
    const ignoreSet = new Set(ignoreColumns);
    const columns = headers.filter((c) => c && !ignoreSet.has(c));

    const rows: Record<string, string>[] = dataRows.map((r) => {
      const obj: Record<string, string> = {};
      headers.forEach((col, i) => {
        if (!col || ignoreSet.has(col)) return;
        obj[col] = normalizeValue(r[i]);
      });
      return obj;
    });

    const nonEmpty = rows.filter((r) => Object.values(r).some((v) => v !== ""));
    return { columns, rows: nonEmpty };
  });
}

export async function selectRowByKey(
  sheetName: string,
  tableName: string | null,
  keyColumns: string[],
  keyValue: string,
  headerRowCount = 1,
  topRowsToSkip = 0
): Promise<boolean> {
  return Excel.run(async (ctx) => {
    const sheet = ctx.workbook.worksheets.getItem(sheetName);
    let range: Excel.Range;
    if (tableName) {
      range = sheet.tables.getItem(tableName).getRange();
    } else {
      range = sheet.getUsedRange(true);
    }
    range.load(["values", "rowIndex", "columnIndex", "rowCount", "columnCount"]);

    let mergedAreas: Array<{ rowIndex: number; columnIndex: number; rowCount: number; columnCount: number }> = [];
    try {
      const merged = range.getMergedAreasOrNullObject();
      merged.areas.load("items/rowIndex,items/columnIndex,items/rowCount,items/columnCount");
      await ctx.sync();
      if (!merged.isNullObject) {
        mergedAreas = merged.areas.items.map((a) => ({
          rowIndex: a.rowIndex,
          columnIndex: a.columnIndex,
          rowCount: a.rowCount,
          columnCount: a.columnCount,
        }));
      }
    } catch {
      await ctx.sync();
    }

    const rawValues = (range.values ?? []) as unknown[][];
    if (rawValues.length === 0) return false;
    const values: unknown[][] = rawValues.map((r) => [...r]);
    if (mergedAreas.length > 0) {
      propagateMergedValues(values, range.rowIndex, range.columnIndex, mergedAreas);
    }

    const skip = tableName ? 0 : Math.max(0, topRowsToSkip);
    const usable = values.slice(skip);
    const headers = buildHeaders(usable, headerRowCount);
    const keyIdx = keyColumns.map((kc) => headers.findIndex((h) => h === kc));
    if (keyIdx.some((i) => i < 0)) return false;

    const dataRows = usable.slice(headerRowCount);
    const rowOffset = dataRows.findIndex((r) => {
      const candidate: Record<string, string> = {};
      keyColumns.forEach((kc, i) => {
        candidate[kc] = normalizeValue(r[keyIdx[i]]);
      });
      return keyOf(candidate, keyColumns) === keyValue;
    });
    if (rowOffset < 0) return false;

    const targetRow = sheet.getRangeByIndexes(
      range.rowIndex + skip + headerRowCount + rowOffset,
      range.columnIndex,
      1,
      range.columnCount
    );
    targetRow.select();
    sheet.activate();
    await ctx.sync();
    return true;
  });
}

export async function subscribeSheetChanges(
  sheetName: string,
  onChange: () => void,
  debounceMs = 400
): Promise<() => Promise<void>> {
  let timer: ReturnType<typeof setTimeout> | null = null;
  const trigger = () => {
    if (timer !== null) clearTimeout(timer);
    timer = setTimeout(onChange, debounceMs);
  };

  let handlerResult: OfficeExtension.EventHandlerResult<Excel.WorksheetChangedEventArgs> | null = null;

  await Excel.run(async (ctx) => {
    const sheet = ctx.workbook.worksheets.getItem(sheetName);
    handlerResult = sheet.onChanged.add(async () => {
      trigger();
    });
    await ctx.sync();
  });

  return async () => {
    if (timer !== null) clearTimeout(timer);
    if (handlerResult) {
      const h = handlerResult;
      try {
        await Excel.run(h.context, async () => {
          h.remove();
          await h.context.sync();
        });
      } catch {
        // sheet may have been removed; ignore
      }
    }
  };
}

export async function isWorkbookReadOnly(): Promise<boolean> {
  return Excel.run(async (ctx) => {
    const wb = ctx.workbook;
    wb.load("readOnly");
    await ctx.sync();
    return !!wb.readOnly;
  });
}

export { normalizeValue };
